/**
 * Polyploid (autotetraploid) QTL mapping — in-pod production pipeline (Beta).
 * Upload dosage/map/phenotype CSVs (or one-click sample) → async job →
 * genome-wide dosage QTL scan → results (QTL table + Manhattan + CSV).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import SharedNav from '@/components/SharedNav';
import { Play, Upload, Download, Loader2, ArrowLeft, Dna, FlaskConical, History, ChevronDown, ChevronRight, FileText } from 'lucide-react';

const C = {
  green900: '#1B4332', green500: '#2D6A4F', border: '#E7E5E0',
  ink: '#2C2A28', grey: '#5E5A56', amber: '#B45309', amber50: '#FEF3C7',
};

export default function PolyploidQTL() {
  const navigate = useNavigate();
  const [limits, setLimits] = useState(null);
  const [job, setJob] = useState(null);
  const [polling, setPolling] = useState(false);
  const [history, setHistory] = useState([]);
  const [files, setFiles] = useState({ geno: null, map: null, pheno: null });
  const [openQtl, setOpenQtl] = useState(null);
  const [ploidy, setPloidy] = useState(4);
  const [formatSpec, setFormatSpec] = useState(null);
  const [showFormats, setShowFormats] = useState(false);
  const pollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/polyploid/jobs`, { withCredentials: true });
      setHistory(r.data.jobs || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    axios.get(`${API}/polyploid/limits`, { withCredentials: true })
      .then((r) => setLimits(r.data)).catch(() => {});
    loadHistory();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadHistory]);

  useEffect(() => {
    axios.get(`${API}/polyploid/format-spec?ploidy=${ploidy}`, { withCredentials: true })
      .then((r) => setFormatSpec(r.data)).catch(() => {});
  }, [ploidy]);

  const downloadTemplate = async (kind) => {
    try {
      const r = await axios.get(`${API}/polyploid/template/${kind}?ploidy=${ploidy}`,
        { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `polyploid_${kind}_template_${ploidy}x.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(`Could not download the ${kind} template`); }
  };

  const pollJob = useCallback((jobId) => {
    setPolling(true);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/polyploid/jobs/${jobId}`, { withCredentials: true });
        setJob(r.data);
        if (r.data.status === 'done' || r.data.status === 'error') {
          clearInterval(pollRef.current);
          setPolling(false);
          loadHistory();
          if (r.data.status === 'done') toast.success(`Scan complete — ${r.data.result?.n_qtl_detected ?? 0} QTL detected`);
          else toast.error(`Scan failed: ${r.data.error}`);
        }
      } catch (e) {
        clearInterval(pollRef.current); setPolling(false);
        toast.error('Lost contact with the job');
      }
    }, 1500);
  }, [loadHistory]);

  const runSample = async () => {
    try {
      setJob(null); setOpenQtl(null);
      const fd = new FormData();
      fd.append('ploidy', String(ploidy));
      const r = await axios.post(`${API}/polyploid/jobs/sample`, fd, { withCredentials: true });
      toast.info(`Sample ${ploidy}x scan queued…`);
      pollJob(r.data.job_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start sample scan'); }
  };

  const runUpload = async (e) => {
    e.preventDefault();
    if (!files.geno || !files.map || !files.pheno) { toast.error('Please attach all three CSV files'); return; }
    const fd = new FormData();
    fd.append('geno', files.geno); fd.append('map', files.map); fd.append('pheno', files.pheno);
    fd.append('ploidy', String(ploidy));
    try {
      setJob(null); setOpenQtl(null);
      const r = await axios.post(`${API}/polyploid/jobs`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.info(`Scan (${ploidy}x) queued…`);
      pollJob(r.data.job_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  const downloadCsv = async () => {
    if (!job?.job_id) return;
    try {
      const r = await axios.get(`${API}/polyploid/jobs/${job.job_id}/export.csv`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = `polyploid_qtl_${job.job_id.slice(0,8)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('CSV export failed'); }
  };

  const result = job?.status === 'done' ? job.result : null;

  return (
    <div style={{ minHeight: '100vh', background: '#FBFAF8' }} data-testid="polyploid-qtl-page">
      <SharedNav />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 24px' }}>
        <button onClick={() => navigate('/molecular-genetics')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: C.grey, cursor: 'pointer', marginBottom: 14, fontSize: 13 }} data-testid="polyploid-qtl-back">
          <ArrowLeft size={14} /> Molecular Genetics
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <Dna size={26} color={C.green500} />
          <h1 className="font-display" style={{ fontSize: 28, color: C.green900, margin: 0 }}>
            Autopolyploid QTL Mapping <span style={{ fontSize: 13, background: C.amber50, color: C.amber, padding: '2px 8px', borderRadius: 20, verticalAlign: 'middle' }}>Beta</span>
          </h1>
        </div>
        <p style={{ color: C.grey, fontSize: 14, marginBottom: 18, lineHeight: 1.6 }}>
          Genuinely <strong>polysomic</strong> dosage QTL scan for autotetraploid (4x) &amp; autohexaploid (6x) data:
          dosages {`{0…4}`}/{`{0…6}`}, single-QTL interval mapping under the <strong>full genetic model
          (additive + dominance)</strong> plus a joint <strong>multiple-QTL model (CIM)</strong>, with a permutation
          threshold — run as an async job.
        </p>

        {limits && (
          <div style={{ background: C.amber50, borderLeft: `3px solid ${C.amber}`, borderRadius: 8, padding: 12, fontSize: 12.5, color: C.amber, marginBottom: 22, lineHeight: 1.6 }} data-testid="polyploid-qtl-limits">
            <strong>In-pod feasible scale:</strong> up to {limits.max_markers} markers × {limits.max_individuals} individuals,
            {' '}{limits.max_permutations} permutations. {limits.note}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }} data-testid="polyploid-ploidy-selector">
          <span style={{ fontSize: 13, fontWeight: 600, color: C.green900 }}>Ploidy level</span>
          <div style={{ display: 'inline-flex', border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', background: 'white' }}>
            {[[4, '4x · Tetraploid', 'potato, rose, alfalfa'], [6, '6x · Hexaploid', 'sweet potato, wheat, blueberry']].map(([val, lbl, eg]) => {
              const on = ploidy === val;
              return (
                <button key={val} onClick={() => setPloidy(val)} disabled={polling}
                  data-testid={`polyploid-ploidy-${val}x`}
                  style={{ padding: '8px 16px', border: 'none', cursor: polling ? 'wait' : 'pointer',
                    background: on ? C.green500 : 'white', color: on ? 'white' : C.grey,
                    fontSize: 13, fontWeight: on ? 700 : 500, transition: 'background .2s' }}
                  title={eg}>
                  {lbl}
                </button>
              );
            })}
          </div>
          <span style={{ fontSize: 12, color: C.grey }}>Dosages {ploidy === 6 ? '{0…6}' : '{0…4}'}</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18, marginBottom: 26 }}>
          {/* Sample */}
          <div style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }} data-testid="polyploid-qtl-sample-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <FlaskConical size={18} color={C.green500} />
              <h3 style={{ fontSize: 15, fontWeight: 700, color: C.green900, margin: 0 }}>Try a sample dataset</h3>
            </div>
            <p style={{ fontSize: 13, color: C.grey, marginBottom: 14, lineHeight: 1.6 }}>
              Simulated {ploidy === 6 ? 'hexaploid' : 'tetraploid'} F1 family ({ploidy}x, 180 individuals, 3 linkage groups, 2 planted QTL). One click, ~2&nbsp;seconds.
            </p>
            <button onClick={runSample} disabled={polling} className="btn-press"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 8, background: C.green500, color: 'white', border: 'none', fontWeight: 600, fontSize: 13, cursor: polling ? 'wait' : 'pointer' }}
              data-testid="polyploid-qtl-run-sample">
              <Play size={15} /> Run sample scan
            </button>
          </div>

          {/* Upload */}
          <div style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }} data-testid="polyploid-qtl-upload-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Upload size={18} color={C.green500} />
              <h3 style={{ fontSize: 15, fontWeight: 700, color: C.green900, margin: 0 }}>Upload your data</h3>
            </div>
            <form onSubmit={runUpload}>
              {[['geno', `Genotype CSV (individuals × markers, dosage 0–${ploidy})`],
                ['map', 'Map CSV (marker, chromosome, position_cM)'],
                ['pheno', 'Phenotype CSV (id, trait…)']].map(([k, label]) => (
                <label key={k} style={{ display: 'block', fontSize: 12, color: C.grey, marginBottom: 8 }}>
                  {label}
                  <input type="file" accept=".csv" onChange={(e) => setFiles({ ...files, [k]: e.target.files[0] })}
                    style={{ display: 'block', marginTop: 3, fontSize: 12 }} data-testid={`polyploid-qtl-file-${k}`} />
                </label>
              ))}
              <button type="submit" disabled={polling} className="btn-press"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 8, background: C.green900, color: 'white', border: 'none', fontWeight: 600, fontSize: 13, cursor: polling ? 'wait' : 'pointer', marginTop: 4 }}
                data-testid="polyploid-qtl-run-upload">
                <Play size={15} /> Run scan
              </button>
            </form>
          </div>
        </div>

        {/* File formats & templates */}
        <div style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 26, overflow: 'hidden' }} data-testid="polyploid-formats-panel">
          <button onClick={() => setShowFormats(!showFormats)}
            data-testid="polyploid-formats-toggle"
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
            <FileText size={18} color={C.green500} />
            <span style={{ fontSize: 15, fontWeight: 700, color: C.green900 }}>Input file formats &amp; templates</span>
            <span style={{ fontSize: 12, color: C.grey, marginLeft: 4 }}>({ploidy}x · 3 CSV files)</span>
            <span style={{ marginLeft: 'auto', color: C.grey }}>
              {showFormats ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </span>
          </button>

          {/* quick download row (always visible) */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '0 18px 14px' }}>
            {[['genotype', 'Genotype'], ['map', 'Map'], ['phenotype', 'Phenotype']].map(([k, lbl]) => (
              <button key={k} onClick={() => downloadTemplate(k)} className="btn-press"
                data-testid={`polyploid-template-${k}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 8, background: C.green50 || '#EAF3EE', color: C.green900, border: `1px solid ${C.border}`, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                <Download size={13} /> {lbl} template ({ploidy}x)
              </button>
            ))}
          </div>

          {showFormats && formatSpec && (
            <div style={{ padding: '4px 18px 20px', borderTop: `1px solid ${C.border}` }} data-testid="polyploid-formats-detail">
              <p style={{ fontSize: 12.5, color: C.grey, margin: '12px 0 16px', lineHeight: 1.6 }}>
                Three CSV files are required. All files use plain comma-separated values with a header row.
                Individual ids must match between the genotype and phenotype files, and marker names must match
                between the genotype headers and the map. Dosages are integers in <strong>{`{0…${formatSpec.dosage_range[1]}}`}</strong> for {ploidy}x.
              </p>
              {formatSpec.files.map((f) => (
                <div key={f.kind} style={{ marginBottom: 18 }} data-testid={`polyploid-format-${f.kind}`}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <code style={{ fontSize: 12.5, fontWeight: 700, color: C.green900, background: '#F1F5F9', padding: '2px 7px', borderRadius: 5 }}>{f.filename}</code>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{f.title}</span>
                  </div>
                  <p style={{ fontSize: 12, color: C.grey, margin: '0 0 8px', lineHeight: 1.55 }}>{f.description}</p>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead>
                      <tr style={{ background: '#F8FAF9', textAlign: 'left' }}>
                        <th style={{ padding: '6px 10px', fontWeight: 600 }}>Column</th>
                        <th style={{ padding: '6px 10px', fontWeight: 600 }}>Example</th>
                        <th style={{ padding: '6px 10px', fontWeight: 600 }}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {f.columns.map((c, ci) => (
                        <tr key={ci} style={{ borderTop: `1px solid ${C.border}` }}>
                          <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{c.name}</td>
                          <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: C.grey }}>{c.example}</td>
                          <td style={{ padding: '6px 10px', color: C.grey }}>{c.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Running / results */}
        {job && (
          <div style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, marginBottom: 26 }} data-testid="polyploid-qtl-results">
            {(job.status === 'queued' || job.status === 'running') && (
              <div style={{ textAlign: 'center', color: C.grey, padding: 20 }}>
                <Loader2 size={22} className="animate-spin" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} />
                Scan {job.status}…
              </div>
            )}
            {job.status === 'error' && (
              <div style={{ color: '#B91C1C', fontSize: 14 }} data-testid="polyploid-qtl-error">Scan failed: {job.error}</div>
            )}
            {result && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                  <h3 style={{ fontSize: 17, fontWeight: 700, color: C.green900, margin: 0 }}>
                    Results — {result.trait} · {result.n_qtl_detected} QTL detected
                  </h3>
                  <button onClick={downloadCsv} className="btn-press"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, background: C.green500, color: 'white', border: 'none', fontSize: 13, cursor: 'pointer' }}
                    data-testid="polyploid-qtl-download-csv">
                    <Download size={14} /> QTL table (CSV)
                  </button>
                </div>
                <div style={{ fontSize: 13, color: C.grey, marginBottom: 14 }}>
                  <span style={{ background: C.amber50, color: C.amber, padding: '2px 8px', borderRadius: 20, fontWeight: 700, marginRight: 8 }}>{result.ploidy}x</span>
                  {result.n_individuals} individuals × {result.n_markers} markers · {result.n_chromosomes} linkage groups ·
                  {' '}{result.n_permutations} permutations · 95% LOD threshold = <strong>{result.lod_threshold_95}</strong>
                </div>
                {result.manhattan_png && (
                  <img src={`data:image/png;base64,${result.manhattan_png}`} alt="Genome-wide LOD"
                    style={{ width: '100%', maxWidth: 980, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 16 }}
                    data-testid="polyploid-qtl-manhattan" />
                )}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-testid="polyploid-qtl-table">
                  <thead>
                    <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                      <th style={thd}></th>
                      <th style={thd}>LG</th><th style={thd}>Peak marker</th><th style={thd}>Pos (cM)</th>
                      <th style={thd}>LOD</th><th style={thd}>PVE % (add / dom)</th>
                      <th style={thd}>Gene action</th><th style={thd}>Add. effect</th><th style={thd}>Support (cM)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.qtl_table.length === 0 && (
                      <tr><td colSpan={9} style={{ padding: 14, color: C.grey }}>No QTL cleared the permutation threshold.</td></tr>
                    )}
                    {result.qtl_table.map((q, i) => {
                      const open = openQtl === i;
                      const ga = q.gene_action || 'Additive';
                      const gaColor = ga.startsWith('Additive') ? '#2D6A4F'
                        : ga.startsWith('Partial') ? '#B45309' : '#7C3AED';
                      const gaBg = ga.startsWith('Additive') ? '#E6F0E9'
                        : ga.startsWith('Partial') ? '#FEF3C7' : '#F1EAFB';
                      return (
                        <React.Fragment key={`${q.chromosome}-${i}`}>
                          <tr style={{ borderBottom: open ? 'none' : `1px solid ${C.border}`, cursor: 'pointer' }}
                            onClick={() => setOpenQtl(open ? null : i)}
                            data-testid={`polyploid-qtl-row-${i}`}>
                            <td style={{ ...tdd, width: 26, color: C.grey }}>
                              {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                            </td>
                            <td style={{ ...tdd, fontWeight: 600 }}>{q.chromosome}</td>
                            <td style={tdd}>{q.peak_marker}</td>
                            <td style={{ ...tdd, fontFamily: 'monospace' }}>{q.position_cM}</td>
                            <td style={{ ...tdd, fontFamily: 'monospace' }}>{q.lod}</td>
                            <td style={{ ...tdd, fontFamily: 'monospace' }}>
                              {q.pve_percent}
                              <span style={{ color: C.grey, fontSize: 11.5 }}>
                                {' '}({q.pve_additive ?? '–'} / {q.dominance_pve ?? '–'})
                              </span>
                            </td>
                            <td style={tdd}>
                              <span style={{ fontSize: 11.5, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                                background: gaBg, color: gaColor }} data-testid={`polyploid-qtl-geneaction-${i}`}>
                                {ga}
                              </span>
                            </td>
                            <td style={{ ...tdd, fontFamily: 'monospace' }}>{q.additive_effect}</td>
                            <td style={{ ...tdd, fontFamily: 'monospace' }}>{q.support_interval_cM?.join('–')}</td>
                          </tr>
                          {open && (
                            <tr style={{ borderBottom: `1px solid ${C.border}`, background: '#FBFAF8' }}
                              data-testid={`polyploid-qtl-detail-${i}`}>
                              <td></td>
                              <td colSpan={8} style={{ padding: '10px 12px 16px' }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: C.green900, marginBottom: 8 }}>
                                  Dosage-class means at {q.peak_marker}
                                  <span style={{ fontWeight: 400, color: C.grey }}>
                                    {' '}· full-model df {q.model_df ?? '–'} · additive LOD {q.lod_additive} · dominance LOD {q.lod_dominance}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  {(q.class_means || []).map((cm) => {
                                    const means = (q.class_means || []).map(c => c.mean);
                                    const lo = Math.min(...means), hi = Math.max(...means);
                                    const frac = hi > lo ? (cm.mean - lo) / (hi - lo) : 0.5;
                                    return (
                                      <div key={cm.dosage} style={{ flex: '1 1 90px', minWidth: 84, border: `1px solid ${C.border}`,
                                        borderRadius: 8, padding: '8px 10px', background: 'white' }}>
                                        <div style={{ fontSize: 11, color: C.grey }}>Dosage {cm.dosage} <span style={{ color: '#9C9791' }}>(n={cm.n})</span></div>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, fontFamily: 'monospace' }}>{cm.mean}</div>
                                        <div style={{ height: 4, borderRadius: 4, marginTop: 5, background: '#EEECE8' }}>
                                          <div style={{ height: '100%', borderRadius: 4, width: `${Math.round(frac * 100)}%`,
                                            background: gaColor, transition: 'width .3s' }} />
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                <p style={{ fontSize: 11.5, color: C.grey, marginTop: 10, lineHeight: 1.6 }} data-testid="polyploid-qtl-model-note">
                  QTL detected under the full genetic model (dosage as a {result.ploidy === 6 ? '{0…6}' : '{0…4}'} factor). <strong>PVE %</strong> shows total
                  variance explained, with the <strong>additive / dominance</strong> partition in brackets. Click a row to see
                  the per-dosage-class trait means. Gene action is called from the dominance share of explained variance.
                </p>

                {/* per-QTL allele dosage-effect plots */}
                {result.effect_plot_png && (
                  <div style={{ marginTop: 24 }} data-testid="polyploid-effect-plots-section">
                    <h4 style={{ fontSize: 15, fontWeight: 700, color: C.green900, margin: '0 0 4px' }}>Allele dosage-effect plots</h4>
                    <p style={{ fontSize: 12.5, color: C.grey, margin: '0 0 10px', lineHeight: 1.6 }}>
                      Observed dosage-class means ± SE with the fitted additive line — the gap between the points and the
                      line is the <strong>dominance deviation</strong>.
                    </p>
                    <img src={`data:image/png;base64,${result.effect_plot_png}`} alt="Per-QTL dosage-effect plots"
                      style={{ width: '100%', maxWidth: 980, border: `1px solid ${C.border}`, borderRadius: 8 }}
                      data-testid="polyploid-effect-plot-img" />
                  </div>
                )}

                {/* multiple-QTL (CIM) model */}
                {result.multi_qtl_model && (
                  <div style={{ marginTop: 26 }} data-testid="polyploid-multiqtl-section">
                    <h4 style={{ fontSize: 15, fontWeight: 700, color: C.green900, margin: '0 0 4px' }}>
                      Multiple-QTL model (CIM · forward–backward selection)
                    </h4>
                    <p style={{ fontSize: 12.5, color: C.grey, margin: '0 0 10px', lineHeight: 1.6 }}>
                      A joint additive model fitted by forward–backward selection. Each QTL's <strong>conditional LOD</strong>
                      {' '}and <strong>partial PVE</strong> are computed with the other QTL held in the model, so linked/background
                      QTL are controlled for (composite interval mapping). Entry/stay threshold = <strong>{result.multi_qtl_model.entry_threshold}</strong>.
                    </p>
                    {result.multi_qtl_model.n_qtl > 0 ? (
                      <>
                        <div style={{ fontSize: 13, color: C.green900, fontWeight: 600, marginBottom: 8 }}>
                          Joint model PVE (R²): {result.multi_qtl_model.joint_pve_percent}% · {result.multi_qtl_model.n_qtl} QTL retained
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-testid="polyploid-multiqtl-table">
                          <thead>
                            <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                              <th style={thd}>LG</th><th style={thd}>Marker</th><th style={thd}>Pos (cM)</th>
                              <th style={thd}>Conditional LOD</th><th style={thd}>Partial PVE %</th><th style={thd}>Add. effect</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.multi_qtl_model.qtl.map((q, i) => (
                              <tr key={`${q.marker}-${i}`} style={{ borderBottom: `1px solid ${C.border}` }} data-testid={`polyploid-multiqtl-row-${i}`}>
                                <td style={{ ...tdd, fontWeight: 600 }}>{q.chromosome}</td>
                                <td style={tdd}>{q.marker}</td>
                                <td style={{ ...tdd, fontFamily: 'monospace' }}>{q.position_cM}</td>
                                <td style={{ ...tdd, fontFamily: 'monospace' }}>{q.conditional_lod}</td>
                                <td style={{ ...tdd, fontFamily: 'monospace' }}>{q.partial_pve_percent}</td>
                                <td style={{ ...tdd, fontFamily: 'monospace' }}>{q.additive_effect}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    ) : (
                      <div style={{ fontSize: 13, color: C.grey }} data-testid="polyploid-multiqtl-empty">
                        {result.multi_qtl_model.note || 'No QTL retained in the multiple-QTL model.'}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }} data-testid="polyploid-qtl-history">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <History size={16} color={C.grey} />
              <h3 style={{ fontSize: 14, fontWeight: 700, color: C.green900, margin: 0 }}>Recent scans</h3>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <tbody>
                {history.map((h) => (
                  <tr key={h.job_id} style={{ borderTop: `1px solid ${C.border}`, cursor: 'pointer' }}
                    onClick={() => { setJob(null); pollJob(h.job_id); }}>
                    <td style={tdd}>{h.filename}</td>
                    <td style={tdd}>{h.source}</td>
                    <td style={tdd}>{h.status === 'done' ? `${h.n_qtl_detected} QTL` : h.status}</td>
                    <td style={{ ...tdd, color: C.grey }}>{h.created_at ? new Date(h.created_at).toLocaleString() : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const thd = { padding: '8px 10px', fontWeight: 600 };
const tdd = { padding: '8px 10px' };
